<template>
    <div ref="el" class="dropdown">
        <slot name="trigger" v-bind="{ isOpen, changeState, id: 'dd-' + id }" />
        <teleport to="#app" :disabled="!onBody">
            <div
                v-if="isOpen"
                ref="elContent"
                class="card dropdown__content scrollbar-style"
                :aria-labelledby="'dd-' + id"
            >
                <slot name="content" />
            </div>
        </teleport>
    </div>
</template>

<script lang="ts">
import { ref, nextTick, defineComponent } from 'vue';
import { containsTargetElement, focusFirstTabIndex } from '@projectfive/services';
import { createPopper } from '@popperjs/core/lib/popper-lite';
import flip from '@popperjs/core/lib/modifiers/flip';
import preventOverflow from '@popperjs/core/lib/modifiers/preventOverflow';

/**
 * returns new dropdown state, toggles if no new state is explicitly provided
 * @param from current state (opened = true)
 * @param to new supplied state. if not provided from will be inverted
 * @returns new state
 */
function getNewState(from: boolean, to: boolean): boolean {
    return typeof to === 'boolean'
        ? to
        : !from;
}

/**
 * shows contentEl dynamically based on position in DOM
 * @param triggerEl element which the content should stick to
 * @param contentEl element to be positioned
 * @param options popperjs options
 * @returns popperjs instance
 */
function setPositioning(triggerEl: HTMLElement, contentEl: HTMLElement, options = {}) {
    return createPopper(triggerEl, contentEl, {
        modifiers: [flip, preventOverflow],
        ...options,
    });
}

export default defineComponent({
    props: {
        onBody: Boolean,
        id: {
            type: String,
            default: 'dropdown',
        },
        placement: {
            type: String,
            default: 'bottom-start',
        },
    },
    setup(props) {
        const el = ref(null);
        const elContent = ref(null);

        const isOpen = ref(false);
        let popper = null;

        /**
         * change dropdown state and execute functions onchange.
         * @param to new state, toggles if not provided. true = open, false = closed
         */
        function changeState(to?: boolean): void {
            const newState = getNewState(isOpen.value, to);
            isOpen.value = newState;

            if (newState) {
                nextTick(onOpen);
            } else {
                onClose();
            }
        }

        function onOpen(): void {
            popper = setPositioning(el.value, elContent.value, { placement: props.placement });
            document.addEventListener('click', onClick);
            elContent.value.addEventListener('keydown', onKeyPress);
            focusFirstTabIndex(elContent.value);
        }

        function onClose(): void {
            document.removeEventListener('click', onClick);
            elContent.value.removeEventListener('keydown', onKeyPress);
            if (popper) { popper.destroy(); popper = null; }
        }

        function onKeyPress(event): void {
            if (event.key === 'Escape') {
                changeState(false);
            }
        }

        function onClick(event): void {
            if (!containsTargetElement(el.value, event)) {
                changeState(false);
            }
        }

        return {
            isOpen,
            changeState,

            el,
            elContent,
        };
    },
});
</script>

<style>
.dropdown {
    position: relative;
}
    .dropdown__content {
        text-align: left;
        min-width: 12rem;
        position: absolute;
        z-index: 20;
        list-style: none;
        max-height: 25rem;
        overflow: auto;
    }
</style>
